import {css} from '@emotion/react'
import {useStaticQuery, graphql} from 'gatsby'
import CheckIcon from '../../../images/startpage/icons/check.svg'

const translations = {
    en: {
        title: 'Chats stay only on your device',
        benefits: [
            {
                text: 'No server-side storage',
            },
            {
                text: 'AI model selection',
            },
            {
                text: 'Fast, reliable private answers',
            },
        ],
        bottomTitle: '...and disappear with a zap.',
        bottomSubtitle: 'Erase all your chat history instantly with a single tap. No traces left behind.',
    },
    de: {
        title: 'Chats bleiben nur auf Ihrem Gerät',
        benefits: [
            {
                text: 'Keine serverseitige Speicherung',
            },
            {
                text: 'KI-Modell-Auswahl',
            },
            {
                text: 'Schnelle, zuverlässige private Antworten',
            },
        ],
        bottomTitle: '...und verschwinden mit einem Blitz.',
        bottomSubtitle: 'Löschen Sie Ihren gesamten Chat-Verlauf sofort mit einem Tippen. Keine Spuren bleiben zurück.',
    },
    nl: {
        title: 'Chats blijven alleen op uw apparaat',
        benefits: [
            {
                text: 'Geen server-side opslag',
            },
            {
                text: 'AI-model selectie',
            },
            {
                text: 'Snelle, betrouwbare privé antwoorden',
            },
        ],
        bottomTitle: '...en verdwijnen met een bliksem.',
        bottomSubtitle: 'Wis al uw chatgeschiedenis onmiddellijk met een enkele tik. Geen sporen achtergelaten.',
    },
}

// eslint-disable-next-line no-unused-vars
const Section3 = ({languageCode = 'en', experimentId, os, browser}) => {
    const text = translations[languageCode] || translations.en

    const images = useStaticQuery(graphql`
        query PrivateAISection3Images {
            spLogo: file(relativePath: {eq: "startpage/sp-logo-3.svg"}) {
                publicURL
            }
        }
    `)

    return (
        <section
            className="sp-section3"
            css={css`
                width: 100%;
                height: 706px;
                background: linear-gradient(0deg, #29358F 0%, #000 80%);
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                padding: 80px 20px;
                box-sizing: border-box;
                gap: 80px;

                @media (max-width: 768px) {
                    gap: 10px;
                    padding: 60px 10px;
                }
            `}
        >
            {/* Top Section - Benefits */}
            <div
                className="top-section"
                css={css`
                    max-width: 400px;
                    width: 100%;
                    text-align: center;
                `}
            >
                <h2
                    css={css`
                        color: #F2F3FB;
                        text-align: center;
                        leading-trim: both;
                        text-edge: cap;
                        font-family: 'Inter';
                        font-size: 32px;
                        font-style: normal;
                        font-weight: 700;
                        line-height: 40px;
                        letter-spacing: 0.5px;
                        margin: 0 40px 48px 40px;
                        position: relative;
                        z-index: 3;

                        @media (max-width: 380px) {
                            margin-bottom: 40px;
                        }
                    `}
                >
                    {text.title}
                </h2>

                <div
                    className="benefits-list"
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 20px;
                        align-items: center;
                        width: fit-content;
                        margin: 0 auto;
                        position: relative;
                        z-index: 3;
                    `}
                >
                    {text.benefits.map((benefit, index) => (
                        <div
                            key={index}
                            className="benefit-item"
                            css={css`
                                display: flex;
                                align-items: center;
                                gap: 16px;
                                width: 100%;
                            `}
                        >
                            <CheckIcon
                                css={css`
                                    width: 24px;
                                    height: 24px;
                                    flex-shrink: 0;
                                `}
                            />
                            <p
                                css={css`
                                    color: #EBECF7;
                                    font-family: 'Inter';
                                    font-size: 16px;
                                    font-style: normal;
                                    font-weight: 400;
                                    line-height: 21px;
                                    letter-spacing: 0.048px;
                                    margin: 0;
                                    text-align: left;
                                `}
                            >
                                {benefit.text}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Bottom Section - Magic Tap */}
            <div
                className="bottom-section"
                css={css`
                    max-width: 400px;
                    width: 100%;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                `}
            >
                <div
                    className="icon-container"
                    css={css`
                        position: relative;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        padding: 40px;
                    `}
                >
                    {/* Eclipse background effect */}
                    <div
                        css={css`
                            position: absolute;
                            width: 438.738px;
                            height: 438.738px;
                            border-radius: 438.738px;
                            background: rgba(85, 11, 200, 0.5);
                            filter: blur(93.26923370361328px);
                            z-index: 0;
                        `}
                    />

                    <img
                        src={images.spLogo.publicURL}
                        alt="Startpage Logo"
                        css={css`
                            width: 80px;
                            height: 80px;
                            position: relative;
                            z-index: 2;
                        `}
                    />
                </div>
                <h2
                    css={css`
                        color: #F2F3FB;
                        text-align: center;
                        leading-trim: both;
                        text-edge: cap;
                        font-family: 'Inter';
                        font-size: 32px;
                        font-style: normal;
                        font-weight: 700;
                        line-height: 40px;
                        letter-spacing: 0.5px;
                        margin: 0;
                        position: relative;
                        z-index: 3;

                        @media (max-width: 380px) {
                            font-size: 24px;
                        }
                    `}
                >
                    {text.bottomTitle}
                </h2>

                <p
                    css={css`
                        color: #EBECF7;
                        text-align: center;
                        font-family: 'Inter';
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 400;
                        line-height: 24px;
                        margin: 0;
                        max-width: 320px;
                        position: relative;
                        z-index: 3;

                        @media (max-width: 380px) {
                            max-width: 280px;
                        }
                    `}
                >
                    {text.bottomSubtitle}
                </p>
            </div>
        </section>
    )
}

export default Section3
